/**
 * ReservationManagement.jsx — 예약 관리 (Phase 2 — 목록, 2026-04-30)
 *
 * 권한: AIRTEL_RESERVATION_MANAGEMENT (admin은 자동 통과)
 *
 * 기능:
 *   - 검색: 여행코드 / 예약자명
 *   - 필터: 출발일 범위, 상태
 *   - 페이지네이션 (20건/페이지)
 *   - 행 클릭 → /admin/reservations/:code 상세
 *
 * API: GET /api/admin/reservations?q=&date_from=&date_to=&status=&page=&page_size=
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import FieldSettingsModal from './reservationmgmt/FieldSettingsModal';
import CardPaymentDepositModal from './reservationmgmt/CardPaymentDepositModal';
import MobileReservationList from './reservationmgmt/MobileReservationList';
import {
  amountTd,
  compactTd,
  dateFilterInputStyle,
  dateRangeStyle,
  filterGridStyle,
  filterPanelStyle,
  filterPrimaryButtonStyle,
  filterSecondaryButtonStyle,
  filterSelectStyle,
  headerActionsStyle,
  headerStyle,
  listShellStyle,
  pageStyle,
  paginationStyle,
  pgBtn,
  primaryActionStyle,
  receiptTd,
  searchInputStyle,
  secondaryActionStyle,
  subtitleStyle,
  th,
  titleStyle,
  td,
} from './reservationmgmt/ReservationManagement.styles';

const PAGE_SIZE = 20;

// 숫자 포맷 (3자리 콤마) — 사용자 정책 (memory: feedback_number_format)
const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString('ko-KR'));
// 날짜 포맷 'YYYY-MM-DD' (DB DATE 컬럼은 string으로 옴)
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '-');
const fmtEventCount = (n) => (Number(n || 0) > 0 ? `+${fmt(n)}` : '-');
const paymentLabel = (row) => {
  const label = { cash: '현금', card: '카드', mixed: '혼합' }[row?.payment_method];
  if (label) return label;
  if (String(row?.card_payment_status || '').trim()) return '카드';
  return '-';
};
const receiptCancelText = (row) => {
  const number = String(row?.cash_receipt_cancel_approval_no || '').trim();
  const amount = Number(row?.cash_receipt_cancel_amount || 0);
  if (!number && amount === 0 && !row?.has_missing_cash_receipt_cancel) return '-';
  return `${number || '번호 없음'} · ${fmt(amount)}`;
};
const QUICK_FILTERS = [
  { value: '', label: '빠른 필터' },
  { value: 'cancel_fee', label: '취소수수료 있음' },
  { value: 'refund', label: '환불 있음' },
  { value: 'negative_difference', label: '차액 음수' },
  { value: 'missing_cash_receipt', label: '현영번호 없음' },
  { value: 'missing_cash_receipt_cancel', label: '현영취소번호 없음' },
];

const getIsMobile = () => Boolean(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches);

export default function ReservationManagement() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // 검색 입력 (즉시 반영 X) + 적용된 검색 조건 (fetch 트리거)
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cardDepositOpen, setCardDepositOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(getIsMobile);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q)        params.set('q', q);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      if (status)   params.set('status', status);
      if (quickFilter) params.set('quick_filter', quickFilter);
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      const res = await apiClient.get(`/api/admin/reservations?${params.toString()}`);
      if (res.ok && res.data?.success) {
        setRows(res.data.data.rows);
        setTotal(res.data.data.total);
      } else {
        setRows([]); setTotal(0);
      }
    } catch (e) {
      console.error('[ReservationManagement] load error', e);
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo, status, quickFilter, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(max-width: 720px)');
    const sync = () => setIsMobile(media.matches);
    sync();
    if (media.addEventListener) media.addEventListener('change', sync);
    else media.addListener(sync);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', sync);
      else media.removeListener(sync);
    };
  }, []);

  function applySearch() {
    setQ(qInput.trim());
    setPage(1); // 검색 시 1페이지로
  }
  function clearSearch() {
    setQInput(''); setQ('');
    setDateFrom(''); setDateTo(''); setStatus('');
    setQuickFilter('');
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={pageStyle(isMobile)}>
      {/* 헤더 — 직원 관리 표준: 텍스트 제목 + 부제(menuConfig desc) + 우측 액션 */}
      <div style={headerStyle(isMobile)}>
        <div>
          <h2 style={titleStyle}>예약 관리</h2>
          <div style={subtitleStyle}>여행 예약을 관리합니다.</div>
        </div>
        <div style={headerActionsStyle(isMobile)}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>전체 {fmt(total)}건</span>
          <button onClick={() => setSettingsOpen(true)}
            style={secondaryActionStyle(isMobile)}>
            입력항목 설정
          </button>
          <button onClick={() => setCardDepositOpen(true)}
            style={secondaryActionStyle(isMobile)}>
            카드입금 관리
          </button>
          <button onClick={() => navigate('/admin/reservations/new')}
            style={primaryActionStyle(isMobile)}>
            + 예약 등록
          </button>
        </div>
      </div>

      {/* 검색/필터 */}
      <div style={filterPanelStyle(isMobile)}>
        <div style={filterGridStyle(isMobile)}>
          <input
            type="text" placeholder="여행코드 또는 예약자명"
            value={qInput} onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
            style={searchInputStyle(isMobile)}
          />
          <div style={dateRangeStyle(isMobile)}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', flex: '0 0 auto' }}>출발</label>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              style={dateFilterInputStyle(isMobile)} />
            <span style={{ color: 'var(--text-muted)' }}>—</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              style={dateFilterInputStyle(isMobile)} />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            style={filterSelectStyle(isMobile)}>
            <option value="">전체 상태</option>
            <option value="confirmed">확정</option>
            <option value="cancelled">취소</option>
            <option value="completed">완료</option>
          </select>
          <select value={quickFilter} onChange={(e) => { setQuickFilter(e.target.value); setPage(1); }}
            style={filterSelectStyle(isMobile)}>
            {QUICK_FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <button onClick={applySearch}
            style={filterPrimaryButtonStyle(isMobile)}>
            검색
          </button>
          <button onClick={clearSearch}
            style={filterSecondaryButtonStyle(isMobile)}>
            초기화
          </button>
        </div>
      </div>

      {/* 목록 */}
      <div style={listShellStyle}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>예약 없음</div>
        ) : isMobile ? (
          <MobileReservationList
            rows={rows}
            onOpen={(travelCode) => navigate(`/admin/reservations/${encodeURIComponent(travelCode)}`)}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              {/* 머리글: 표준 검정 띠 (stdTh 기반 th) */}
              <thead>
                <tr>
                  <th style={th}>여행코드</th>
                  <th style={th}>예약자</th>
                  <th style={th}>예약일</th>
                  <th style={{ ...th, textAlign: 'right' }}>항공</th>
                  <th style={{ ...th, textAlign: 'right' }}>렌트</th>
                  <th style={{ ...th, textAlign: 'right' }}>호텔</th>
                  <th style={{ ...th, textAlign: 'right' }}>고객지불</th>
                  <th style={{ ...th, textAlign: 'right' }}>차액</th>
                  <th style={{ ...th, textAlign: 'right' }}>수익이벤트</th>
                  <th style={{ ...th, textAlign: 'right' }}>이벤트반영</th>
                  <th style={{ ...th, textAlign: 'right' }}>수익</th>
                  <th style={th}>구분</th>
                  <th style={th}>현금영수증번호</th>
                  <th style={{ ...th, textAlign: 'right' }}>현금영수증금액</th>
                  <th style={th}>현영취소</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.travel_code}
                    onClick={() => navigate(`/admin/reservations/${encodeURIComponent(r.travel_code)}`)}
                    /* 행 구분선: 표준 연한 밑줄, 마지막 행 밑줄 제거 */
                    style={{ cursor: 'pointer', borderBottom: i < rows.length - 1 ? '1px solid var(--bg-subtle)' : 'none' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={compactTd}>{r.travel_code}</td>
                    <td style={td}>{r.customer_name}</td>
                    <td style={compactTd}>{fmtDate(r.reservation_date)}</td>
                    <td style={amountTd}>{fmt(r.flight_total)}</td>
                    <td style={amountTd}>{fmt(r.car_total)}</td>
                    <td style={amountTd}>{fmt(r.hotel_total)}</td>
                    <td style={amountTd}>{fmt(r.customer_paid)}</td>
                    <td style={amountTd}>{fmt(r.difference)}</td>
                    <td style={amountTd}>{fmtEventCount(r.revenue_extra_event_count)}</td>
                    <td style={{ ...amountTd, color: Number(r.revenue_extra_event_amount) < 0 ? '#dc2626' : 'var(--text)' }}>{fmt(r.revenue_extra_event_amount)}</td>
                    <td style={{ ...amountTd, color: Number(r.profit) < 0 ? '#dc2626' : 'var(--text)' }}>{fmt(r.profit)}</td>
                    <td style={compactTd}>{paymentLabel(r)}</td>
                    <td style={receiptTd} title={r.cash_receipt_issue_no || ''}>{r.cash_receipt_issue_no || '-'}</td>
                    <td style={amountTd}>{fmt(r.cash_receipt_amount)}</td>
                    <td
                      style={{
                        ...receiptTd,
                        color: r.has_missing_cash_receipt_cancel ? '#dc2626' : 'var(--text)',
                      }}
                      title={r.cash_receipt_cancel_approval_no || ''}
                    >
                      {receiptCancelText(r)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      <div style={paginationStyle(isMobile)}>
        <button onClick={() => setPage(1)} disabled={page <= 1} style={pgBtn}>«</button>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} style={pgBtn}>‹</button>
        <span style={{ color: 'var(--text)' }}>{fmt(page)} / {fmt(totalPages)}</span>
        <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} style={pgBtn}>›</button>
        <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} style={pgBtn}>»</button>
      </div>
      {settingsOpen && <FieldSettingsModal onClose={() => setSettingsOpen(false)} />}
      <CardPaymentDepositModal open={cardDepositOpen} onClose={() => setCardDepositOpen(false)} />
    </div>
  );
}
